// Booking rules, notification switches, open/close online booking, and the LINE message quota meter.
import { useEffect, useState } from 'react';
import { ApiError } from '../api/client.ts';
import type { Quota, Settings } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { useShopPaths } from '../app/nav.tsx';
import { message } from '../lib/errors.ts';
import { Banner, Button, Card, Field, Loading, Page, useToast } from '../ui/index.tsx';

type NumKey = 'bookingWindowDays' | 'staffBookingWindowDays' | 'leadTimeMinutes' | 'cancelCutoffMinutes' | 'maxUpcomingBookings'
  | 'reminderMinutesBefore' | 'noShowBlockThreshold';
const NUMBERS: [NumKey, string, string][] = [
  ['bookingWindowDays', 'จองล่วงหน้าได้ (วัน)', '1–60'],
  ['leadTimeMinutes', 'ต้องจองล่วงหน้าอย่างน้อย (นาที)', '0–1440'],
  ['cancelCutoffMinutes', 'ยกเลิก/เลื่อนได้ก่อนเวลานัด (นาที)', '0–2880'],
  ['maxUpcomingBookings', 'ลูกค้าจองล่วงหน้าได้สูงสุด (รายการ)', '1–10'],
  ['reminderMinutesBefore', 'แจ้งเตือนก่อนนัด (นาที)', '30–1440'],
  ['noShowBlockThreshold', 'ระงับการจองออนไลน์เมื่อไม่มาครบ (ครั้ง)', '1–10'],
  ['staffBookingWindowDays', 'พนักงานจองล่วงหน้าได้ (วัน)', '1–180'],
];
type BoolKey = 'notifyCustomerConfirmation' | 'notifyCustomerReminder' | 'notifyStaffGroup';
const TOGGLES: [BoolKey, string][] = [
  ['notifyCustomerConfirmation', 'ส่งข้อความยืนยันการจองให้ลูกค้า'],
  ['notifyCustomerReminder', 'ส่งข้อความเตือนก่อนนัด'],
  ['notifyStaffGroup', 'แจ้งเตือนกลุ่มพนักงานเมื่อมีจอง/ยกเลิก'],
];

export function SettingsPage() {
  const { api } = useShop();
  const { go } = useShopPaths();
  const toast = useToast();
  const [saved, setSaved] = useState<Settings | null>(null);
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [quota, setQuota] = useState<Quota | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api.staff.settings().then((a) => {
      setSaved(a.settings);
      setForm(Object.fromEntries(Object.entries(a.settings).map(([k, v]) => [k, typeof v === 'number' ? String(v) : v])));
    }).catch((e) => setLoadError(message(e)));
    api.staff.quota().then(setQuota).catch(() => setQuota(null));
  }, [api]);

  if (loadError) return <Page title="กติกาการจอง" back={() => go('/staff/manage')}><Banner kind="error">{loadError}</Banner></Page>;
  if (!saved) return <Page title="กติกาการจอง" back={() => go('/staff/manage')}><Loading /></Page>;

  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!saved) return;
    setErrors({});
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) {
      const old = (saved as unknown as Record<string, unknown>)[k];
      const val = typeof old === 'number' ? Number(v) : v;
      if (val !== old) patch[k] = val;
    }
    if (Object.keys(patch).length === 0) { toast('ไม่มีการเปลี่ยนแปลง'); return; }
    setBusy(true);
    try {
      setSaved(await api.staff.updateSettings(patch as Partial<Settings>));
      toast('บันทึกแล้ว', 'success');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'VALIDATION' && e.field) setErrors({ [e.field]: 'ค่าไม่อยู่ในช่วงที่อนุญาต' });
      else toast(message(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  const pct = quota?.limited && quota.limit ? Math.min(100, Math.round((quota.used / quota.limit) * 100)) : 0;

  return (
    <Page title="กติกาการจอง" back={() => go('/staff/manage')}>
      <Card>
        <label className="check">
          <input type="checkbox" aria-label="เปิดรับจองออนไลน์" checked={form.onlineBookingOpen === true} onChange={(e) => set('onlineBookingOpen', e.target.checked)} />
          <span><b>เปิดรับจองออนไลน์</b><br /><span className="muted small">ปิดไว้ระหว่างตั้งค่าร้าน ลูกค้าจะยังจองไม่ได้</span></span>
        </label>
      </Card>
      <p className="section-title">กติกา</p>
      {NUMBERS.map(([k, label, range]) => (
        <Field key={k} label={label} hint={`ช่วงที่ตั้งได้ ${range}`} error={errors[k]}>
          <input type="number" inputMode="numeric" value={String(form[k] ?? '')} onChange={(e) => set(k, e.target.value)} />
        </Field>
      ))}
      <Field label="ช่วงเวลาให้ลูกค้าเลือก" error={errors.slotStepMinutes}>
        <select value={String(form.slotStepMinutes)} onChange={(e) => set('slotStepMinutes', e.target.value)}>
          <option value="15">ทุก 15 นาที</option><option value="30">ทุก 30 นาที</option><option value="60">ทุก 1 ชั่วโมง</option>
        </select>
      </Field>
      <div className="row">
        <Field label="นัดก่อนเวลานี้ เตือนคืนก่อน" error={errors.earlySlotCutoff}>
          <input type="time" value={String(form.earlySlotCutoff ?? '')} onChange={(e) => set('earlySlotCutoff', e.target.value)} />
        </Field>
        <Field label="เวลาเตือนคืนก่อน" error={errors.eveningReminderTime}>
          <input type="time" value={String(form.eveningReminderTime ?? '')} onChange={(e) => set('eveningReminderTime', e.target.value)} />
        </Field>
      </div>
      <p className="section-title">ข้อความ LINE</p>
      {TOGGLES.map(([k, label]) => (
        <label key={k} className="check">
          <input type="checkbox" checked={form[k] === true} onChange={(e) => set(k, e.target.checked)} />
          <span>{label}</span>
        </label>
      ))}
      {quota && quota.limited && quota.limit !== null && (
        <Card>
          <p className="small" style={{ marginBottom: 6 }}>ใช้ไปแล้ว {quota.used} / {quota.limit} ข้อความเดือนนี้</p>
          <div className={`meter${quota.warn ? ' warn' : ''}`}><span style={{ width: `${pct}%` }} /></div>
          {quota.warn && <p className="field-msg error" style={{ marginTop: 6 }}>ใกล้ครบโควต้า พิจารณาปิดข้อความที่ไม่จำเป็นหรืออัปเกรดแพ็กเกจ LINE OA</p>}
        </Card>
      )}
      <div style={{ height: 16 }} />
      <Button block loading={busy} onClick={() => void save()}>บันทึกการตั้งค่า</Button>
    </Page>
  );
}
